import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Accessibility, ArrowLeft, Check, CreditCard, Eye, EyeOff, Hand, Loader2, MessageSquare,
  Minus, Plus, ShoppingBag, Trash2, Utensils, X,
} from 'lucide-react';
import { formatCurrencyWith } from '../../utils/dateFormat';
import { useT } from '../../context/LanguageContext';
import { withGuestPrices, taxNames } from '../../lib/taxMath';
import WalletQrScanner from '../../components/customer/WalletQrScanner';
import AdSlotMonster from '../../components/customer/AdSlotMonster';
import { DineInMonster, TakeAwayMonster } from '../../components/customer/ServiceModeMonsters';
import LoweredScreenMonster from '../../components/customer/LoweredScreenMonster';
import '../../styles/kiosk.css';

/* ============================================================
 * SELF-ORDER KIOSK — the standing panel at the counter.
 *
 * This is NOT the guest menu with bigger type. The guest menu is a phone in
 * somebody's hand: they are sitting down, they are signed in, they have a
 * table, and they can put it in their pocket half way through. A kiosk is a
 * 1080 x 1920 panel bolted to a counter, used once, standing up, by a
 * stranger who will walk away mid-order — so it is its own screen with its
 * own rules:
 *
 *   - ONE full-bleed column. No sidebar, no centred narrow column with
 *     gutters: 1080px minus the 48px bezel margin, all of it.
 *   - ANONYMOUS UNTIL IT MATTERS. Nothing is asked before the menu — the
 *     number and the name come after the cart, on the way to paying, where
 *     they are what puts the order on a receipt and against a customer
 *     record. Never a gate in front of the food.
 *   - PAID LIKE A PHONE. The payment step is the guest menu's payment sheet
 *     at kiosk size: the same three methods, the same wallet QR, the same
 *     demo. A guest who has paid on their phone here before recognises it.
 *   - ASKED, then fed. Eat in or take away is answered before a menu is
 *     reachable, because the kitchen packs the two differently, and it rides
 *     on the order (RestaurantOrder.service_mode).
 *   - FORGETFUL. Sixty seconds without a touch and it asks; twenty more and
 *     it throws the whole session away and goes back to the attract screen.
 *     A cart left standing must never become the next person's bill.
 *
 * It talks to the same public endpoints the QR menu does and posts the same
 * order payload, so a kiosk order is an ordinary order with a channel of
 * KIOSK — nothing downstream needs to know this screen exists.
 * ============================================================ */

const API_URL = process.env.REACT_APP_API_URL || '/api';

const STEP = {
  ATTRACT: 'attract',
  MODE: 'mode',
  MENU: 'menu',
  CART: 'cart',
  // Who the order belongs to, asked between the cart and the money.
  WHO: 'who',
  PAY: 'pay',
  DONE: 'done',
};

/* The attract screen used to open on a thali picture shipped inside the app —
   one restaurant's dish advertised on every kiosk of every restaurant. A branch
   now brings its own (Settings -> Kiosk Settings), and a branch that
   has brought none gets the ad-slot monster instead: a drawn placeholder that
   says what the space is for rather than filling it with somebody else's lunch.
   One slide, holding its turn as long as a poster would. */
const PLACEHOLDER_SLIDE = { id: 'ad-slot', kind: 'PLACEHOLDER' };
const ATTRACT_SLIDE_MS = 2000;

// The two answers, exactly as the server's RestaurantOrder.SERVICE_MODES.
const EAT_IN = 'EAT_IN';
const TAKE_AWAY = 'TAKE_AWAY';

/* The server's answer when this screen asks as a kiosk but no registered
   machine stamped the request (apps/orders/order_service.py). The Kiosk
   application adds the device token at the network layer; a browser tab, an
   unpaired panel or a de-registered one gets this instead of a menu. */
const KIOSK_NOT_REGISTERED = 'KIOSK_NOT_REGISTERED';

/* How long a screen waits, until the branch's own clock arrives. A kiosk
   queue moves, so the prompt comes quickly; the countdown is long enough for
   somebody reading the menu to reach out. Each branch sets its own numbers
   under Settings -> Kiosk Settings; they ride in on the menu payload as
   context.kiosk_idle, and these two run only until that has loaded. */
const IDLE_S_DEFAULT = 60;
const COUNTDOWN_S_DEFAULT = 20;

const money = (n) => formatCurrencyWith(n, null);

/* Every price the panel shows is what the guest pays, and every order line
   carries the net (lib/taxMath withGuestPrices): the bill adds the tax back
   on top, so the guest pays exactly the figure on the card. */

/* The Mauritian wallets, by the name they are known by — the same two the
   guest menu's payment sheet offers, spelled the same way, because this is
   deliberately the same payment in a different pair of hands. */
const WALLET_NAMES = { juice: 'Juice', myt: 'my.t money' };

/* A panel bolted to a counter has no keyboard. Both pads are laid out for a
   thumb at standing height: digits big enough to hit without looking, letters
   in the order everybody already knows. */
const DIGITS = ['1', '2', '3', '4', '5', '6', '7', '8', '9', '+', '0', 'back'];
const LETTERS = 'QWERTYUIOPASDFGHJKLZXCVBNM'.split('');

// Loose on purpose: a Mauritian mobile is 8 digits, a landline 7, and a guest
// may type the country code. Anything shorter is a slip, not a number.
const PHONE_MIN_DIGITS = 7;

/* Every second digit as an x, so a number typed on a panel at head height
   cannot be read off it by the person next in the queue. The guest keeps
   enough to recognise their own number — the first digit, and every other
   one after it — which is what a confirmation is for.

   Digits only: a leading + and any spaces keep their places, or what is left
   stops looking like a phone number at all. Exported for its own test, and
   kept away from state: what is SENT is always the real `phone`, and this
   touches nothing but what is drawn. */
export function maskAlternateDigits(value) {
  let seen = 0;
  return String(value == null ? '' : value).replace(/\d/g, (d) => (seen++ % 2 ? 'x' : d));
}

// Cheapest variant of a dish, which is the price the card shows.
const fromPrice = (item) => {
  const prices = (item.variants || []).map((v) => (v.is_free ? 0 : Number(v.price) || 0));
  return prices.length ? Math.min(...prices) : null;
};

const cartKey = (variantId, addons) =>
  `${variantId}:${addons.map((a) => `${a.addon_id}x${a.qty || 1}`).sort().join(',')}`;

// Every cart line that belongs to this menu item, whatever its variant,
// add-ons or note: the same dish in three sizes is three lines, one dish.
const lineOf = (item) => (l) => (item.dish_id != null ? l.dishId === item.dish_id : l.lineId === item.id);

// Anything on the dish the guest has to decide - more than one variant, or
// add-ons on the one there is. A dish without choices can only ever be ONE
// cart line, so its tile never has to ask which.
const hasChoices = (item) => {
  const variants = item.variants || [];
  return variants.length > 1 || (variants[0]?.addons || []).length > 0;
};

export default function KioskOrder() {
  const { t, locale, setLocale } = useT();
  const params = useMemo(() => new URLSearchParams(window.location.search), []);
  const restaurantId = params.get('restaurant') || '';
  const locationId = params.get('location') || '';
  /* WHICH kiosk this poster is. Printed into the code by Settings > QR
     Codes, sent back with every order so the board can name the machine
     rather than only saying a machine took it. */
  const kioskSlug = params.get('kiosk') || '';

  const [menu, setMenu] = useState(null);
  const [context, setContext] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const [step, setStep] = useState(STEP.ATTRACT);
  const [serviceMode, setServiceMode] = useState('');
  const [activeSection, setActiveSection] = useState('all');
  const [cart, setCart] = useState([]);
  const [sheet, setSheet] = useState(null);      // { item, variantId, addonQty, qty, note }
  /* The dish whose cart lines the guest is choosing between - the "Which
     one?" panel behind a tile's − or + when that dish is in the cart in more
     than one way. The item, not a line: the panel lists its lines live. */
  const [which, setWhich] = useState(null);
  /* The dish that was JUST added, with what the kitchen pairs it with - shown
     as its own panel after the add, never before: a guest who has not yet
     decided on the dish is not shopping for what goes with it. */
  const [suggest, setSuggest] = useState(null);  // { item, pairings }
  // Pairing pictures that failed to load - drawn as the placeholder instead
  // of the browser's broken-image icon.
  const [deadPairImgs, setDeadPairImgs] = useState(() => new Set());
  const [a11y, setA11y] = useState(false);
  const [phone, setPhone] = useState('');
  /* Hidden until the guest asks for it. The other way round reads as the
     polite default, but it only protects the guest who spots the control
     BEFORE typing — and by then the number is already on the panel. */
  const [hideNumber, setHideNumber] = useState(true);
  // Optional, and it stays optional: the receipt reads better with it, the
  // order works exactly the same without it.
  const [name, setName] = useState('');
  // Which way they are paying. Card first, like the guest menu's sheet.
  const [payMethod, setPayMethod] = useState('card');
  const [placing, setPlacing] = useState(false);
  const [placed, setPlaced] = useState(null);
  const [orderError, setOrderError] = useState('');

  /* The attract slideshow: which slide is up, and which posters turned out
     not to exist. Runs only while the attract screen is showing, and starts
     again from the first poster each time the panel comes back to it. */
  const [slide, setSlide] = useState(0);
  const [deadPosters, setDeadPosters] = useState(() => new Set());

  /* What this BRANCH uploaded under Settings -> Kiosk Settings,
     ridden in on the menu payload so the attract screen needs no second call.
     A branch that has added nothing keeps the slide shipped with the app —
     an empty counter screen would be worse than a generic one. */
  const ads = useMemo(() => {
    const rows = Array.isArray(context?.kiosk_ads) ? context.kiosk_ads : [];
    const live = rows.filter((a) => a && a.url && !deadPosters.has(a.url));
    return live.length ? live : [PLACEHOLDER_SLIDE];
  }, [context, deadPosters]);

  const slideCount = ads.length;
  const current = ads[slide];
  /* A poster or clip dropping out from under the slide that is up would
     otherwise leave the index pointing at nothing, and the screen blank. */
  useEffect(() => {
    if (slide >= slideCount) setSlide(0);
  }, [slide, slideCount]);
  /* A poster gets its two seconds; a CLIP holds the screen until it ends, so
     the timer stands down and `onEnded` moves the rotation on. A clip that
     cannot play (codec, autoplay refused) would otherwise freeze the loop, so
     it drops out of the rotation the same way a missing poster does. */
  useEffect(() => {
    if (step !== STEP.ATTRACT || slideCount < 2) return undefined;
    if (current && current.kind === 'CLIP') return undefined;
    const id = setInterval(() => setSlide((sl) => (sl + 1) % slideCount), ATTRACT_SLIDE_MS);
    return () => clearInterval(id);
  }, [step, slideCount, current]);

  // Back to the top of the rotation each time the panel returns to attract.
  useEffect(() => {
    if (step === STEP.ATTRACT) setSlide(0);
  }, [step]);

  // The clock in the header, its own state so the once-a-second tick repaints
  // a span rather than the menu behind it.
  const [now, setNow] = useState(() => new Date());
  useEffect(() => {
    const id = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(id);
  }, []);

  // ------------------------------------------------------------- data ----
  useEffect(() => {
    if (!restaurantId || !locationId) {
      setError(t('kiosk.error.notSetUp'));
      setLoading(false);
      return;
    }
    let alive = true;
    // This screen IS a kiosk, so it asks as one: only the menus ticked
    // for kiosks come back (Menu editor -> Kiosk).
    fetch(`${API_URL}/users/public/restaurant/${restaurantId}/location/${locationId}/menu/?kiosk=1`)
      .then((r) => r.json())
      .then((data) => {
        if (!alive) return;
        // No registered machine behind this screen: the server shows it no
        // menu at all. Keyed on the code so the sentence is in the guest's
        // language, not the server's.
        if (data?.code === KIOSK_NOT_REGISTERED) { setError(t('kiosk.error.notRegistered')); return; }
        if (!data?.success) { setError(data?.error || t('kiosk.error.noMenu')); return; }
        setMenu(withGuestPrices(data.data));
        setContext(data.context || null);
      })
      .catch(() => { if (alive) setError(t('kiosk.error.offline')); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [restaurantId, locationId, t]);

  // ----------------------------------------------------------- cancel ----
  /* The Cancel button in the top bar: the guest's own way out, as opposed
     to the idle clock's. With nothing in the order it goes straight back to
     the attract screen; with something in it the panel asks first, because
     a big button at a kiosk gets brushed by sleeves. */
  const [confirmCancel, setConfirmCancel] = useState(false);

  // ------------------------------------------------------------- idle ----
  /* Everything the session knows, thrown away together. Called by the idle
     countdown, by the Cancel button and by the guest finishing — one place,
     so a new piece of session state can never be left behind on the panel
     for the next person to find. */
  const resetSession = useCallback(() => {
    setConfirmCancel(false);
    setCart([]);
    setSheet(null);
    setWhich(null);
    setSuggest(null);
    setServiceMode('');
    setActiveSection('all');
    setPhone('');
    // The next guest starts covered, whatever the last one chose.
    setHideNumber(true);
    setName('');
    setPayMethod('card');
    setPlaced(null);
    setOrderError('');
    setStep(STEP.ATTRACT);
  }, []);

  const [prompt, setPrompt] = useState(0);       // 0 = not asking, else seconds left
  const idleTimer = useRef(null);
  const countTimer = useRef(null);
  const stepRef = useRef(step);
  stepRef.current = step;

  const clearTimers = () => {
    clearTimeout(idleTimer.current);
    clearInterval(countTimer.current);
    idleTimer.current = null;
    countTimer.current = null;
  };

  /* The branch's clock (Settings -> Kiosk Settings), read off the menu
     payload like the ads. Whole seconds, validated server-side; the defaults
     cover the moment before the payload lands. */
  const idleMs = (Number(context?.kiosk_idle?.idle_seconds) || IDLE_S_DEFAULT) * 1000;
  const countdownS = Number(context?.kiosk_idle?.countdown_seconds) || COUNTDOWN_S_DEFAULT;

  /* Restart the clock. Bound to the whole screen's touches below, so any tap
     anywhere counts as "still here" — including a tap inside the prompt. */
  const poke = useCallback(() => {
    clearTimers();
    setPrompt(0);
    if (stepRef.current === STEP.ATTRACT) return;   // nothing to time out
    idleTimer.current = setTimeout(() => {
      setPrompt(countdownS);
      countTimer.current = setInterval(() => {
        setPrompt((s) => {
          if (s <= 1) {
            clearTimers();
            resetSession();
            return 0;
          }
          return s - 1;
        });
      }, 1000);
    }, idleMs);
  }, [resetSession, idleMs, countdownS]);

  useEffect(() => { poke(); return clearTimers; }, [step, poke]);

  const cancelOrder = () => {
    if (cart.length === 0) {
      clearTimers();
      resetSession();
      return;
    }
    setConfirmCancel(true);
  };

  // ------------------------------------------------------------- cart ----
  const cartCount = useMemo(() => cart.reduce((n, l) => n + l.qty, 0), [cart]);
  const cartTotal = useMemo(
    () => cart.reduce((n, l) => n + l.unitPrice * l.qty, 0), [cart]);
  // The tax inside that total, and the taxes it is made of, named once.
  const cartTax = useMemo(
    () => cart.reduce((n, l) => n + (l.unitPrice - (l.unitNet ?? l.unitPrice)) * l.qty, 0), [cart]);
  const cartTaxNames = useMemo(
    () => Array.from(new Set(cart.map((l) => l.taxText).filter(Boolean))).join(', '), [cart]);

  const sections = menu?.sections || [];
  const shown = useMemo(() => {
    if (activeSection === 'all') return sections;
    return sections.filter((s) => String(s.id) === String(activeSection));
  }, [sections, activeSection]);

  /* Under a card's price, for a dish that names a tax of its OWN: how that
     figure is made, since the panel always shows what the guest pays —
     "incl. VAT 15%" for one priced with the tax inside, "Rs 225.00 + VAT 15%"
     for one priced without it. Every other dish shows just its price; the
     default tax is shown once, at checkout. */
  const taxNote = (item) => {
    const rows = (item.tax && item.tax.labels) || [];
    const vs = (item.variants || []).filter((v) => !v.is_free && Number(v.price) > 0);
    if (!rows.length || !vs.length || !item.tax.own) return null;
    const v = vs.reduce((m, x) => (Number(x.price) < Number(m.price) ? x : m), vs[0]);
    const names = taxNames(rows);
    return item.tax.inclusive
      ? t('kiosk.tax.incl', { taxes: names })
      : t('kiosk.tax.plus', { net: money(Number(v.price_net) || 0), taxes: names });
  };

  const variantLabel = (v) => {
    const base = v.size || v.name || t('kiosk.variant.regular');
    const qty = v.quantity != null && v.unit ? `${v.quantity} ${v.unit}` : '';
    return qty ? `${base} · ${qty}` : base;
  };

  const openSheet = (item) => {
    const variants = item.variants || [];
    setSheet({
      item,
      variantId: variants[0]?.id ?? null,
      addonQty: {},
      qty: 1,
      note: '',
    });
  };

  /* What the kitchen pairs this dish with - the Recommended pairings set on
     the dish (Add On Items) - resolved against THIS menu: a pairing that is
     not on the counter tonight simply does not show, and nothing 86'd here is
     offered. Each one opens its own sheet rather than being added blind,
     because a lassi still has a size to choose and add-ons to decline. */
  const pairingsFor = (item) => {
    const order = new Map((item.recommended || []).map((r, i) => [r.dish_id, i]));
    if (!order.size) return [];
    const seen = new Set();
    const out = [];
    for (const it of (menu?.sections || []).flatMap((s) => s.items || [])) {
      if (!order.has(it.dish_id) || it.dish_id === item.dish_id || seen.has(it.dish_id)) continue;
      if (it.is_available === false || it.available_here === false) continue;
      if (!(it.variants || []).length) continue;
      seen.add(it.dish_id);
      out.push(it);
    }
    return out.sort((a, b) => order.get(a.dish_id) - order.get(b.dish_id)).slice(0, 6);
  };

  /* Straight into the cart when there is nothing to decide — one variant and
     no add-ons. A sheet that only says "1" and "Add" is a tax on the queue. */
  const quickAdd = (item) => {
    const variants = item.variants || [];
    if (variants.length === 1 && (variants[0].addons || []).length === 0) {
      addLine(item, variants[0], {}, 1, '');
      suggestFor(item);
      return;
    }
    openSheet(item);
  };

  /* After a dish goes in: offer what goes with it, if anything does. Nothing
     to offer means straight back to the menu, as before. */
  const suggestFor = (item) => {
    const pairings = pairingsFor(item);
    setSuggest(pairings.length ? { item, pairings } : null);
  };

  /* A pairing picked from that panel. Nothing to decide - one variant, no
     add-ons - and it goes straight in; otherwise its own sheet opens, because
     a lassi still has a size to choose. Either way the panel is done: one
     suggestion per add, never a chain of them. */
  const pickPairing = (p) => {
    setSuggest(null);
    const variants = p.variants || [];
    if (variants.length === 1 && (variants[0].addons || []).length === 0) {
      addLine(p, variants[0], {}, 1, '');
      return;
    }
    openSheet(p);
  };

  const addLine = (item, v, addonQty, qty, note) => {
    const addons = (v.addons || [])
      .map((a) => ({ ...a, qty: Number(addonQty[a.addon_id]) || 0 }))
      .filter((a) => a.qty > 0);
    // The size's own price, kept apart from the unit price so the order
    // screen can show what the unit is made of.
    const variantPrice = v.is_free ? 0 : Number(v.price) || 0;
    const unitPrice = variantPrice
      + addons.reduce((s, a) => s + (Number(a.price) || 0) * a.qty, 0);
    // The same unit without its tax: what the order carries (see withGuestPrices).
    const unitNet = (v.is_free ? 0 : Number(v.price_net ?? v.price) || 0)
      + addons.reduce((s, a) => s + (Number(a.price_net ?? a.price) || 0) * a.qty, 0);
    const taxText = item.tax && item.tax.labels && item.tax.labels.length ? taxNames(item.tax.labels) : '';
    const noteKey = (note || '').trim();
    const key = `${cartKey(v.id, addons)}|${noteKey}`;
    setCart((prev) => {
      const seen = prev.find((l) => l.key === key);
      if (seen) return prev.map((l) => (l.key === key ? { ...l, qty: l.qty + qty } : l));
      return [...prev, {
        key,
        dishId: item.dish_id,
        lineId: item.id,
        variantId: v.id,
        name: item.name,
        variant: variantLabel(v),
        image: item.image || null,
        note: noteKey,
        variantPrice,
        unitPrice,
        unitNet,
        taxText,
        addons,
        qty,
      }];
    });
  };

  const stepLine = (key, by) => setCart((prev) => prev
    .map((l) => (l.key === key ? { ...l, qty: l.qty + by } : l))
    .filter((l) => l.qty > 0));

  /* The minus on a dish tile. One line of that dish in the cart and there is
     nothing to ask: it comes off that line. Several lines - the same wrap as
     a Tortilla, a Naan and a Roti, say - and the tile cannot know which the
     guest means, so it asks: the "Which one?" panel lists those lines, each
     with its own stepper. It used to take from the line added last, which
     was a guess, and a wrong guess at a kiosk is only found at the bill. */
  const takeOne = (item) => {
    const lines = cart.filter(lineOf(item));
    if (lines.length === 1) stepLine(lines[0].key, -1);
    else if (lines.length > 1) setWhich(item);
  };

  /* The plus on a tile that already shows a count. A dish without choices
     just goes in again. A dish WITH choices is not repeated blindly: the
     guest may want one more of the Tortilla they have, or the Naan they do
     not, and the tile cannot tell - so the same panel opens, where each line
     in the cart has its own plus and "Add a different option" opens the
     sheet for a new line. */
  const addOneMore = (item) => {
    if (hasChoices(item)) setWhich(item);
    else quickAdd(item);
  };

  /* The panel closes itself once the last line of its dish is gone - there
     is nothing left to choose between, and the tile is back to a plain +. */
  useEffect(() => {
    if (which && !cart.some(lineOf(which))) setWhich(null);
  }, [cart, which]);

  // ------------------------------------------------------------ order ----
  const placeOrder = async (method) => {
    setPlacing(true);
    setOrderError('');
    try {
      const res = await fetch(`${API_URL}/users/orders/create/`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          restaurant_id: restaurantId,
          // WHICH SCREEN this is. A kiosk order and a guest ordering from
          // their own phone are otherwise the same table-less order on the
          // same endpoint, and the kitchen and the counter both need to tell
          // them apart — somebody is standing at this machine right now.
          origin: 'KIOSK',
          kiosk: kioskSlug,
          // No table: a kiosk order files on the branch's ONLINE counter.
          table_id: null,
          location_id: locationId,
          session_id: null,
          // Asked on the way to paying: the number is the customer record's
          // own key (CRM), the name is what makes the receipt read like a
          // person rather than a token number.
          customer_name: name.trim(),
          customer_phone: phone.trim(),
          menu_ids: [],
          paid: true,
          payment_method: method,
          // What they were asked before the menu opened.
          service_mode: serviceMode,
          notes: '',
          items: cart.map((l) => ({
            dish_id: l.dishId,
            variant_id: l.variantId,
            menu_section_item_id: l.lineId,
            name: l.variant ? `${l.name} (${l.variant})` : l.name,
            quantity: l.qty,
            unit_price: l.unitNet,
            notes: l.note || '',
            addons: l.addons.map((a) => ({
              addon_id: a.addon_id, name: a.name, price: a.price_net ?? a.price, qty: a.qty || 1,
            })),
          })),
        }),
      });
      const data = await res.json();
      if (!res.ok || !data?.success) {
        // De-registered since the menu loaded: the order is refused the same
        // way the menu would have been.
        setOrderError(data?.code === KIOSK_NOT_REGISTERED
          ? t('kiosk.error.notRegistered')
          : (data?.error || t('kiosk.pay.failed')));
        return;
      }
      setPlaced(data.data);
      setStep(STEP.DONE);
    } catch {
      setOrderError(t('kiosk.error.offline'));
    } finally {
      setPlacing(false);
    }
  };

  // --------------------------------------------------------- rendering ---
  /* Render HELPERS, not components: the clock re-renders this page every
     second, and a component declared in here would be a new type each time —
     React would throw the subtree away under a finger already on it. */

  const clockLabel = () => {
    const h = now.getHours();
    return `${h % 12 || 12}:${String(now.getMinutes()).padStart(2, '0')} ${h >= 12 ? 'PM' : 'AM'}`;
  };

  const hoursLine = () => {
    const open = context?.opens_at || context?.open_time;
    const close = context?.closes_at || context?.close_time;
    return open && close ? t('kiosk.header.hours', { open, close }) : '';
  };

  const renderLang = () => (
    <div className="k-lang">
      {['en', 'fr'].map((code) => (
        <button key={code} type="button" className={locale === code ? 'on' : ''}
          onClick={() => setLocale(code)}>
          {code.toUpperCase()}
        </button>
      ))}
    </div>
  );

  const renderA11yToggle = () => (
    <button type="button" className={`k-icon-btn${a11y ? ' on' : ''}`}
      aria-label={t('kiosk.a11y.toggle')} onClick={() => setA11y((v) => !v)}>
      <Accessibility size={40} />
    </button>
  );

  const renderCancelBtn = () => (
    <button type="button" className="k-cancel-btn" onClick={cancelOrder}>
      <X size={34} /> {t('kiosk.cancel.button')}
    </button>
  );

  const renderModeChip = () => (serviceMode ? (
    <button type="button" className="k-mode-chip" onClick={() => setStep(STEP.MODE)}>
      {serviceMode === EAT_IN ? <Utensils size={30} /> : <ShoppingBag size={30} />}
      {serviceMode === EAT_IN ? t('kiosk.mode.eatIn') : t('kiosk.mode.takeAway')}
      <small>{t('kiosk.mode.change')}</small>
    </button>
  ) : null);

  const renderHeader = () => (
    <header className="k-header">
      <div className="k-brand">
        <span className="k-logo">
          {context?.restaurant_logo
            ? <img src={context.restaurant_logo} alt="" />
            : <Utensils size={48} />}
        </span>
        <span style={{ minWidth: 0 }}>
          <span className="k-brand-name">
            {context?.restaurant_name || menu?.restaurant_name || 'RestoBOXX'}
          </span>
          {context?.location_name ? (
            <span className="k-brand-sub">{context.location_name}</span>
          ) : null}
        </span>
      </div>
      <div className="k-head-right">
        {renderModeChip()}
        <span className="k-clockbox">
          <span className="k-clock">{clockLabel()}</span>
          {hoursLine() ? <span className="k-hours">{hoursLine()}</span> : null}
        </span>
        {renderLang()}
        {renderA11yToggle()}
        {renderCancelBtn()}
      </div>
    </header>
  );

  const renderCard = (item) => {
    const available = item.is_available !== false && item.available_here !== false;
    const veg = !!(item.is_vegan || item.is_vegetarian);
    const price = fromPrice(item);
    const note = taxNote(item);
    const inCart = cart.filter(lineOf(item)).reduce((n, l) => n + l.qty, 0);
    const allergens = (item.allergens || []).join(', ');
    return (
      <div key={item.id} className={`k-card${available ? '' : ' out'}`}>
        {item.image
          ? <img className="k-card-img" src={item.image} alt="" />
          : <div className="k-card-img ph"><Utensils size={72} /></div>}
        <div className="k-card-body">
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: 16 }}>
            <span className={`k-veg${veg ? '' : ' no'}`} aria-hidden="true"><i /></span>
            <span className="k-card-name">{item.name}</span>
          </div>
          {item.description ? <div className="k-card-desc">{item.description}</div> : null}
          {allergens ? (
            <div className="k-card-tags">{t('kiosk.item.contains', { list: allergens })}</div>
          ) : null}
        </div>
        <div className="k-card-foot">
          <span className="k-price">
            {price == null ? t('kiosk.item.onRequest') : price === 0 ? t('kiosk.item.free') : money(price)}
            {(item.variants || []).length > 1 ? <small>{t('kiosk.item.fromPrice')}</small> : null}
            {note ? <small className="k-price-tax">{note}</small> : null}
          </span>
          {!available ? (
            <span className="k-card-tags">{t('kiosk.item.soldOut')}</span>
          ) : inCart > 0 ? (
            <span className="k-qty k-card-qty">
              <button type="button" aria-label={t('kiosk.sheet.less')} onClick={() => takeOne(item)}>
                {inCart === 1 ? <Trash2 size={30} /> : <Minus size={32} />}
              </button>
              <span className="k-qty-n">{inCart}</span>
              <button type="button" aria-label={t('kiosk.sheet.more')} onClick={() => addOneMore(item)}>
                <Plus size={32} />
              </button>
            </span>
          ) : (
            <button type="button" className="k-add" aria-label={t('kiosk.item.add')}
              onClick={() => quickAdd(item)}>
              <Plus size={44} />
            </button>
          )}
        </div>
      </div>
    );
  };

  const renderPills = () => (
    <div className="k-pillbar">
      <button type="button" className={`k-pill${activeSection === 'all' ? ' on' : ''}`}
        onClick={() => setActiveSection('all')}>
        {t('kiosk.menu.all')}
      </button>
      {sections.map((s) => (
        <button key={s.id} type="button"
          className={`k-pill${String(activeSection) === String(s.id) ? ' on' : ''}`}
          onClick={() => setActiveSection(String(s.id))}>
          {s.name}
        </button>
      ))}
    </div>
  );

  const renderMenu = () => (
    <div className={`k-scroll${cartCount > 0 ? ' has-cart' : ''}`}>
      {shown.map((s) => (
        <section key={s.id} style={{ marginBottom: 56 }}>
          <h2 className="k-h2">{s.name}</h2>
          <div className="k-grid">{(s.items || []).map(renderCard)}</div>
        </section>
      ))}
      {shown.every((s) => (s.items || []).length === 0) ? (
        <p className="k-sub">{t('kiosk.menu.empty')}</p>
      ) : null}
    </div>
  );

  const renderCartBar = () => (cartCount > 0 && step === STEP.MENU ? (
    <div className="k-cartbar">
      <span className="k-cartbar-l">
        <span className="k-cartbar-count">
          {t('kiosk.cart.items', { count: cartCount })}
        </span>
        <span className="k-cartbar-total">{money(cartTotal)}</span>
      </span>
      <button type="button" className="k-btn dark" onClick={() => setStep(STEP.CART)}>
        <ShoppingBag size={36} /> {t('kiosk.cart.view')}
      </button>
    </div>
  ) : null);

  // ---- the full-screen steps ----

  /* The question sits in the middle of the panel with its two answers side by
     side, and each answer is a monster dressed for it (ServiceModeMonsters):
     the chef-waiter holding a plate up for Dine in, the one holding a bag out
     for Take away. The monster's speech bubble IS the label, so the answer is
     read off what the character says rather than off an icon. */
  const renderMode = () => (
    <div className="k-step k-mode">
      <h1 className="k-h2">{t('kiosk.mode.title')}</h1>
      <div className="k-mode-body">
        {[
          { id: EAT_IN, art: <DineInMonster />, label: t('kiosk.mode.eatIn') },
          { id: TAKE_AWAY, art: <TakeAwayMonster />, label: t('kiosk.mode.takeAway') },
        ].map((o) => (
          <button key={o.id} type="button"
            className={`k-mode-card${serviceMode === o.id ? ' on' : ''}`}
            onClick={() => { setServiceMode(o.id); setStep(STEP.MENU); }}>
            <span className="k-mode-bubble">{o.label}</span>
            <span className="k-mode-art">{o.art}</span>
          </button>
        ))}
      </div>
      {/* No skip and no close: the kitchen packs the two differently, so this
          is the one question the panel will not let anybody past. Only a
          guest who has already answered gets a way back. */}
      {serviceMode ? (
        <button type="button" className="k-btn ghost k-mode-back" onClick={() => setStep(STEP.MENU)}>
          <ArrowLeft size={34} /> {t('kiosk.mode.keep')}
        </button>
      ) : null}
    </div>
  );

  /* "Goes well with", AFTER the add. The dish is in the order; what the
     kitchen pairs it with is offered once, here, and the menu comes back
     either way. It used to sit inside the sheet, above the quantity, where
     it was read by somebody still deciding on the dish itself. */
  const renderSuggest = () => {
    if (!suggest) return null;
    const { item, pairings } = suggest;
    return (
      <div className="k-step k-suggest">
        <div className="k-step-body">
          <div className="k-suggest-ok"><Check size={64} /></div>
          <h1 className="k-h2">{t('kiosk.suggest.title')}</h1>
          <p className="k-sub">{t('kiosk.suggest.sub', { name: item.name })}</p>
          <div className="k-sec-title">{t('kiosk.sheet.pairs')}</div>
          <div className="k-pairs">
            {pairings.map((p) => {
              const price = fromPrice(p);
              const img = p.image && !deadPairImgs.has(p.image) ? p.image : '';
              return (
                <button key={p.dish_id} type="button" className="k-pair" onClick={() => pickPairing(p)}>
                  {img
                    ? <img src={img} alt="" onError={() => setDeadPairImgs((s) => new Set(s).add(p.image))} />
                    : <span className="k-pair-ph" />}
                  <span className="k-pair-name">{p.name}</span>
                  <span className="k-pair-price">
                    {price == null ? t('kiosk.item.onRequest') : price === 0 ? t('kiosk.item.free') : money(price)}
                    {(p.variants || []).length > 1 ? <small> {t('kiosk.item.fromPrice')}</small> : null}
                  </span>
                </button>
              );
            })}
          </div>
        </div>
        <button type="button" className="k-btn ghost block" onClick={() => setSuggest(null)}>
          {t('kiosk.suggest.skip')}
        </button>
      </div>
    );
  };

  const renderSheet = () => {
    if (!sheet) return null;
    const { item } = sheet;
    const variants = item.variants || [];
    const v = variants.find((x) => x.id === sheet.variantId) || variants[0];
    const addons = (v && v.addons) || [];
    const unit = (v ? (v.is_free ? 0 : Number(v.price) || 0) : 0)
      + addons.reduce((s, a) => s + (Number(a.price) || 0) * (Number(sheet.addonQty[a.addon_id]) || 0), 0);
    return (
      <div className="k-step">
        <div className="k-step-body">
          {item.image
            ? <img className="k-sheet-img" src={item.image} alt="" />
            : null}
          <h2 className="k-sheet-name">{item.name}</h2>
          {item.description ? <p className="k-sheet-desc">{item.description}</p> : null}
          {(item.allergens || []).length > 0 ? (
            <p className="k-card-tags" style={{ fontSize: 26 }}>
              {t('kiosk.item.contains', { list: item.allergens.join(', ') })}
            </p>
          ) : null}

          {variants.length > 1 ? (
            <>
              <div className="k-sec-title">{t('kiosk.sheet.choose')}</div>
              {variants.map((x) => (
                <button key={x.id} type="button"
                  className={`k-opt${x.id === sheet.variantId ? ' on' : ''}`}
                  onClick={() => setSheet((s) => ({ ...s, variantId: x.id, addonQty: {} }))}>
                  {x.id === sheet.variantId ? <Check size={34} /> : null}
                  {variantLabel(x)}
                  <span className="k-opt-price">
                    {x.is_free ? t('kiosk.item.free') : money(Number(x.price) || 0)}
                  </span>
                </button>
              ))}
            </>
          ) : null}

          {addons.length > 0 ? (
            <>
              <div className="k-sec-title">{t('kiosk.sheet.extras')}</div>
              {addons.map((a) => {
                const n = Number(sheet.addonQty[a.addon_id]) || 0;
                return (
                  <div key={a.addon_id} className={`k-opt${n > 0 ? ' on' : ''}`}>
                    <span style={{ minWidth: 0 }}>{a.name}</span>
                    <span className="k-opt-price">{money(Number(a.price) || 0)}</span>
                    <span className="k-qty" style={{ marginLeft: 24 }}>
                      {/* None is the floor, and an extra nobody has chosen shows
                          this at 0 — so without the disable every add-on on the
                          sheet offers a minus that does nothing. */}
                      <button type="button" aria-label={t('kiosk.sheet.less')}
                        disabled={n === 0}
                        onClick={() => setSheet((s) => ({
                          ...s,
                          addonQty: { ...s.addonQty, [a.addon_id]: Math.max(0, n - 1) },
                        }))}>
                        <Minus size={36} />
                      </button>
                      <span className="k-qty-n">{n}</span>
                      <button type="button" aria-label={t('kiosk.sheet.more')}
                        onClick={() => setSheet((s) => ({
                          ...s,
                          addonQty: { ...s.addonQty, [a.addon_id]: n + 1 },
                        }))}>
                        <Plus size={36} />
                      </button>
                    </span>
                  </div>
                );
              })}
            </>
          ) : null}

          <div className="k-sec-title">{t('kiosk.sheet.howMany')}</div>
          <div className="k-qty">
            {/* One is the floor here: the dish is not in the order yet, so there
                is nothing for a zero to mean — Cancel is the way out, not a
                minus. Disabled rather than silently refusing, which is what it
                did before: on a panel there is no cursor to show a dead button
                and no way to tell a refusal from a frozen screen. */}
            <button type="button" aria-label={t('kiosk.sheet.less')}
              disabled={sheet.qty <= 1}
              onClick={() => setSheet((s) => ({ ...s, qty: Math.max(1, s.qty - 1) }))}>
              <Minus size={40} />
            </button>
            <span className="k-qty-n">{sheet.qty}</span>
            <button type="button" aria-label={t('kiosk.sheet.more')}
              onClick={() => setSheet((s) => ({ ...s, qty: s.qty + 1 }))}>
              <Plus size={40} />
            </button>
          </div>

          <div className="k-sec-title">{t('kiosk.sheet.note')}</div>
          <textarea
            className="k-note"
            value={sheet.note}
            placeholder={t('kiosk.sheet.notePlaceholder')}
            onChange={(e) => setSheet((s) => ({ ...s, note: e.target.value.slice(0, 140) }))}
          />
        </div>
        <div className="k-btn-row">
          <button type="button" className="k-btn ghost" onClick={() => setSheet(null)}>
            <X size={34} /> {t('kiosk.sheet.cancel')}
          </button>
          <button type="button" className="k-btn block" disabled={!v}
            onClick={() => {
              addLine(item, v, sheet.addonQty, sheet.qty, sheet.note);
              setSheet(null);
              suggestFor(item);
            }}>
            <Plus size={34} /> {t('kiosk.sheet.add', { total: money(unit * sheet.qty) })}
          </button>
        </div>
      </div>
    );
  };

  /* "Which one?" - behind a tile's − or + when the dish is already in the
     cart and has choices. Only THAT dish's lines, each with its own stepper
     (a bin on the last of a line), so the guest changes the line they mean.
     The dish's name is the heading, because the tile the guest just pressed
     is now under the panel.

     Then the two things they are actually here to do, both on this screen:
     have the same again, and have a different one. Both used to mean a trip
     back to the full item sheet to re-choose a size the panel could simply
     have shown — a second screen, standing up, with a queue behind them.

       Same again   repeats the LAST line exactly, add-ons and note included,
                    which is the one thing a variant button cannot do: a bare
                    size makes its own line and would quietly drop the extras.
       The options  every size the dish comes in, priced, adding a line on the
                    spot. A size already in the order merges into that line,
                    so pressing it is also "another one of those".

     The sheet is still one press away for the case neither covers — a
     different size WITH different extras, or a note. */
  const renderWhich = () => {
    if (!which) return null;
    const lines = cart.filter(lineOf(which));
    const options = which.variants || [];
    // The most recent line is what "same again" means; cart order is add order.
    const last = lines[lines.length - 1] || null;
    return (
      <div className="k-step">
        <div className="k-step-body">
          <h1 className="k-h2">{t('kiosk.which.title')}</h1>
          <p className="k-sub">{t('kiosk.which.sub', { name: which.name })}</p>
          {lines.map((l) => (
            <div key={l.key} className="k-line">
              <span className="k-line-body">{renderLineDetails(l, true)}</span>
              <span className="k-qty">
                <button type="button" aria-label={t('kiosk.sheet.less')} onClick={() => stepLine(l.key, -1)}>
                  {l.qty === 1 ? <Trash2 size={34} /> : <Minus size={36} />}
                </button>
                <span className="k-qty-n">{l.qty}</span>
                <button type="button" aria-label={t('kiosk.sheet.more')} onClick={() => stepLine(l.key, 1)}>
                  <Plus size={36} />
                </button>
              </span>
              <span className="k-line-price">{money(l.unitPrice * l.qty)}</span>
            </div>
          ))}

          {last ? (
            <button type="button" className="k-opt k-opt-again"
              onClick={() => stepLine(last.key, 1)}>
              <Plus size={34} />
              <span style={{ minWidth: 0 }}>
                {t('kiosk.which.again')}
                <span className="k-opt-sub">
                  {[last.variant || last.name,
                    ...last.addons.map((a) => (a.qty > 1 ? `${a.qty}× ${a.name}` : a.name))]
                    .filter(Boolean).join(' · ')}
                </span>
              </span>
              <span className="k-opt-price">{money(last.unitPrice)}</span>
            </button>
          ) : null}

          {options.length ? (
            <>
              <div className="k-sec-title">{t('kiosk.which.options')}</div>
              {options.map((v) => (
                <button key={v.id} type="button" className="k-opt"
                  onClick={() => addLine(which, v, {}, 1, '')}>
                  {variantLabel(v)}
                  <span className="k-opt-price">
                    {v.is_free ? t('kiosk.item.free') : money(Number(v.price) || 0)}
                  </span>
                </button>
              ))}
            </>
          ) : null}
        </div>
        <div className="k-btn-row">
          <button type="button" className="k-btn ghost"
            onClick={() => { setWhich(null); openSheet(which); }}>
            <Plus size={34} /> {t('kiosk.which.another')}
          </button>
          <button type="button" className="k-btn block" onClick={() => setWhich(null)}>
            <Check size={34} /> {t('kiosk.which.done')}
          </button>
        </div>
      </div>
    );
  };

  /* What a line is made of, under its name: the size with its own price,
     every extra with its price (× its count), and the note. The figure at
     the line's right edge is its total; this is what that total is OF, so a
     guest sees that Rs 300 is Rs 250 of wrap and Rs 50 of chips here, before
     paying, rather than on the receipt. It used to be one muted run of
     words - "Tortilla · 1 plate · Chips" - with no price on any of them.
     `lead` makes the size the heading, for the "Which one?" panel where the
     dish's name is already the title. */
  const priceText = (n) => (n > 0 ? money(n) : t('kiosk.item.free'));
  const renderLineDetails = (l, lead = false) => (
    <>
      <span className={`k-line-detail${lead ? ' lead' : ''}`}>
        <span className="k-line-detail-label">{l.variant || l.name}</span>
        <span className="k-line-detail-price">{priceText(Number(l.variantPrice) || 0)}</span>
      </span>
      {l.addons.map((a) => (
        <span key={a.addon_id} className="k-line-detail">
          <span className="k-line-detail-label">+ {a.qty > 1 ? `${a.qty}× ` : ''}{a.name}</span>
          <span className="k-line-detail-price">{priceText((Number(a.price) || 0) * a.qty)}</span>
        </span>
      ))}
      {l.note ? <span className="k-line-meta">“{l.note}”</span> : null}
    </>
  );

  const renderCart = () => (
    <div className="k-step">
      <h1 className="k-h2">{t('kiosk.cart.title')}</h1>
      <div className="k-step-body">
        {cart.length === 0 ? <p className="k-sub">{t('kiosk.cart.empty')}</p> : null}
        {cart.map((l) => (
          <div key={l.key} className="k-line">
            <span className="k-line-body">
              <span className="k-line-name">{l.name}</span>
              {renderLineDetails(l)}
            </span>
            <span className="k-qty">
              <button type="button" aria-label={t('kiosk.sheet.less')} onClick={() => stepLine(l.key, -1)}>
                {l.qty === 1 ? <Trash2 size={34} /> : <Minus size={36} />}
              </button>
              <span className="k-qty-n">{l.qty}</span>
              <button type="button" aria-label={t('kiosk.sheet.more')} onClick={() => stepLine(l.key, 1)}>
                <Plus size={36} />
              </button>
            </span>
            <span className="k-line-price">{money(l.unitPrice * l.qty)}</span>
          </div>
        ))}
        {cart.length > 0 ? (
          <>
            <div className="k-total-row">
              <span>{t('kiosk.cart.total')}</span>
              <span>{money(cartTotal)}</span>
            </div>
            {/* What of that total is tax — the same figure the bill breaks out. */}
            {cartTax > 0 ? (
              <div className="k-tax-row">
                <span>{t('kiosk.tax.included', { taxes: cartTaxNames })}</span>
                <span>{money(cartTax)}</span>
              </div>
            ) : null}
          </>
        ) : null}
      </div>
      <div className="k-btn-row">
        <button type="button" className="k-btn ghost" onClick={() => setStep(STEP.MENU)}>
          <ArrowLeft size={34} /> {t('kiosk.cart.more')}
        </button>
        <button type="button" className="k-btn block" disabled={cart.length === 0}
          onClick={() => setStep(STEP.WHO)}>
          <CreditCard size={34} /> {t('kiosk.cart.pay')}
        </button>
      </div>
    </div>
  );

  /* WHO — the number, and a name if they feel like it.
     Its own screen rather than a field on the payment sheet: a keypad big
     enough for a standing stranger fills a phone screen on its own, and
     burying it under the card fields is how it gets skipped. */
  const phoneDigits = phone.replace(/\D/g, '');
  const phoneOk = phoneDigits.length >= PHONE_MIN_DIGITS;

  const tapDigit = (k) => setPhone((v) => {
    if (k === 'back') return v.slice(0, -1);
    if (v.length >= 20) return v;
    // The country code only makes sense in front.
    if (k === '+' && v.length) return v;
    return v + k;
  });

  const tapLetter = (k) => setName((v) => {
    if (k === 'back') return v.slice(0, -1);
    if (v.length >= 40) return v;
    if (k === 'space') return v.length ? `${v} ` : v;
    // Title case as it is typed: nobody at a counter shifts.
    return v + (v.endsWith(' ') || v === '' ? k : k.toLowerCase());
  });

  const renderWho = () => (
    <div className="k-step">
      <h1 className="k-h2">{t('kiosk.who.title')}</h1>
      <p className="k-sub">{t('kiosk.who.sub')}</p>
      <div className="k-step-body">
        <div className="k-sec-title k-sec-row">
          <span>{t('kiosk.who.phone')}</span>
          {/* Only worth offering once there is something to hide. */}
          {phone && (
            <button type="button" className="k-peek"
              onClick={() => setHideNumber((v) => !v)}>
              {hideNumber ? <Eye size={28} /> : <EyeOff size={28} />}
              {hideNumber ? t('kiosk.who.show') : t('kiosk.who.hide')}
            </button>
          )}
        </div>
        <div className="k-entry" aria-live="polite">
          {phone
            ? (hideNumber ? maskAlternateDigits(phone) : phone)
            : <span className="k-entry-ph">{t('kiosk.who.phonePlaceholder')}</span>}
        </div>
        {phone && hideNumber && (
          <p className="k-entry-note">{t('kiosk.who.hiddenNote')}</p>
        )}
        <div className="k-pad k-pad-num">
          {DIGITS.map((k) => (
            <button key={k} type="button" className={`k-key ${k === 'back' ? 'wide' : ''}`}
              aria-label={k === 'back' ? t('kiosk.who.backspace') : k}
              onClick={() => tapDigit(k)}>
              {k === 'back' ? '⌫' : k}
            </button>
          ))}
        </div>

        <div className="k-sec-title">{t('kiosk.who.name')}</div>
        <div className="k-entry small">
          {name || <span className="k-entry-ph">{t('kiosk.who.namePlaceholder')}</span>}
        </div>
        <div className="k-pad k-pad-abc">
          {LETTERS.map((k) => (
            <button key={k} type="button" className="k-key" onClick={() => tapLetter(k)}>{k}</button>
          ))}
          <button type="button" className="k-key wide" onClick={() => tapLetter('space')}>
            {t('kiosk.who.space')}
          </button>
          <button type="button" className="k-key wide" aria-label={t('kiosk.who.backspace')}
            onClick={() => tapLetter('back')}>⌫</button>
        </div>
      </div>
      <div className="k-btn-row">
        <button type="button" className="k-btn ghost" onClick={() => setStep(STEP.CART)}>
          <ArrowLeft size={34} /> {t('kiosk.pay.back')}
        </button>
        <button type="button" className="k-btn block" disabled={!phoneOk}
          onClick={() => setStep(STEP.PAY)}>
          <CreditCard size={34} /> {phoneOk ? t('kiosk.who.continue') : t('kiosk.who.needPhone')}
        </button>
      </div>
    </div>
  );

  /* PAY — the guest menu's payment sheet, at kiosk size. Same three methods,
     same demo, and for a wallet the same code: a panel has no camera to scan
     the counter's sticker with, so WalletQrScanner shows the guest's own code
     for them to pay against — which is the way round these wallets work when
     the till is the one standing still. */
  const renderPay = () => (
    <div className="k-step">
      <h1 className="k-h2">{t('kiosk.pay.title')}</h1>
      <p className="k-sub">{t('kiosk.pay.demo')}</p>
      <div className="k-step-body">
        <div className="k-paytotal">
          <span>{t('kiosk.pay.totalLabel')}</span>
          <strong>{money(cartTotal)}</strong>
          {cartTax > 0 ? (
            <small>{t('kiosk.tax.included', { taxes: cartTaxNames })} · {money(cartTax)}</small>
          ) : null}
        </div>

        <div className="k-methods">
          {[
            ['card', '💳', t('kiosk.pay.methodCard')],
            ['juice', '📱', t('kiosk.pay.methodJuice')],
            ['myt', '🏦', t('kiosk.pay.methodMyt')],
          ].map(([id, icon, label]) => (
            <button key={id} type="button" disabled={placing}
              className={`k-method ${payMethod === id ? 'on' : ''}`}
              aria-pressed={payMethod === id}
              onClick={() => setPayMethod(id)}>
              <span aria-hidden="true">{icon}</span> {label}
            </button>
          ))}
        </div>

        {payMethod === 'card' ? (
          <div className="k-cardform">
            <input className="k-note" type="text" inputMode="numeric" disabled={placing}
              aria-label={t('kiosk.pay.cardNumber')} placeholder="4242 4242 4242 4242" />
            <div className="k-cardform-row">
              <input className="k-note" type="text" inputMode="numeric" disabled={placing}
                aria-label={t('kiosk.pay.cardExpiry')} placeholder="MM / YY" />
              <input className="k-note" type="password" inputMode="numeric" maxLength={4}
                disabled={placing} aria-label={t('kiosk.pay.cardCvv')} placeholder="CVV" />
            </div>
          </div>
        ) : (
          <div className="k-wallet">
            <WalletQrScanner
              wallet={WALLET_NAMES[payMethod] || ''}
              amount={money(cartTotal)}
              onScanned={() => placeOrder(payMethod)}
            />
          </div>
        )}

        <p className="k-sub k-payto">{t('kiosk.pay.receiptTo', { phone })}</p>
        {orderError ? <p className="k-card-tags" style={{ fontSize: 28 }}>{orderError}</p> : null}
      </div>
      <div className="k-btn-row">
        <button type="button" className="k-btn ghost" disabled={placing}
          onClick={() => setStep(STEP.WHO)}>
          <ArrowLeft size={34} /> {t('kiosk.pay.back')}
        </button>
        <button type="button" className="k-btn block" disabled={placing}
          onClick={() => placeOrder(payMethod)}>
          {placing
            ? <><Loader2 size={34} className="animate-spin" /> {t('kiosk.pay.placing')}</>
            : <><CreditCard size={34} /> {t('kiosk.pay.payNow', { total: money(cartTotal) })}</>}
        </button>
      </div>
    </div>
  );

  const renderDone = () => (
    <div className="k-step">
      <div className="k-step-body k-done">
        <Check size={120} color="#1D9E75" />
        <h1 className="k-h2" style={{ marginTop: 24 }}>{t('kiosk.done.title')}</h1>
        <p className="k-sub">{t('kiosk.done.sub')}</p>
        <div className="k-token">
          {placed?.token_number || (placed?.order_number || '').split('-').pop() || '—'}
        </div>
        <p className="k-done-note">
          {t('kiosk.done.number', { number: placed?.order_number || '' })}
        </p>
        <p className="k-done-note" style={{ marginTop: 24 }}>
          {serviceMode === EAT_IN ? t('kiosk.done.eatIn') : t('kiosk.done.takeAway')}
        </p>
        {/* What happens next, off the panel: the number they gave at the WHO
            step is where the order details and the ready call go, so it is
            printed back rather than left as "we'll be in touch". */}
        <p className="k-done-notify">
          <MessageSquare size={30} aria-hidden="true" />
          <span>{t('kiosk.done.notify', { phone: phone.trim() })}</span>
        </p>
        <p className="k-done-enjoy">{t('kiosk.done.enjoy')}</p>
      </div>
      <button type="button" className="k-btn block" onClick={resetSession}>
        <Check size={34} /> {t('kiosk.done.finish')}
      </button>
    </div>
  );

  const renderAttract = () => (
    <div className="k-attract" onClick={() => setStep(STEP.MODE)}>
      {/* The branch's own posters and clips, each a slide of its own with the
          call to action over its foot; a touch anywhere on the screen starts
          an order regardless. */}
      {ads.map((ad, i) => (
        <div key={ad.id || ad.url} className={`k-attract-slide is-poster ${slide === i ? 'is-on' : ''}`}
          aria-hidden={slide !== i}>
          {ad.kind === 'PLACEHOLDER' ? (
            /* Nothing booked on this screen yet. */
            <AdSlotMonster />
          ) : ad.kind === 'CLIP' ? (
            /* Muted and inline because no browser will autoplay otherwise, and
               a counter panel has nobody to press play. Only the slide on
               screen is given a source, so three clips do not all buffer at
               once on a machine that struggles with one. */
            <video className="k-attract-poster" muted playsInline autoPlay
              src={slide === i ? ad.url : undefined}
              onEnded={() => setSlide((sl) => (sl + 1) % slideCount)}
              onError={() => setDeadPosters((d) => new Set(d).add(ad.url))} />
          ) : (
            <img className="k-attract-poster" src={ad.url} alt=""
              onError={() => setDeadPosters((d) => new Set(d).add(ad.url))} />
          )}
          <div className="k-attract-poster-cta">
            <button type="button" className="k-attract-cta k-attract-pulse"
              tabIndex={slide === i ? 0 : -1}
              onClick={() => setStep(STEP.MODE)}>
              <Hand size={48} /> {t('kiosk.attract.ctaNow')}
            </button>
          </div>
        </div>
      ))}
    </div>
  );

  const renderCancelPrompt = () => (confirmCancel ? (
    <div className="k-modal">
      <div className="k-modal-card">
        <div className="k-modal-title">{t('kiosk.cancel.title')}</div>
        <div className="k-modal-body">{t('kiosk.cancel.body')}</div>
        <div className="k-btn-row">
          <button type="button" className="k-btn ghost" onClick={() => setConfirmCancel(false)}>
            {t('kiosk.cancel.keep')}
          </button>
          <button type="button" className="k-btn block dark"
            onClick={() => { clearTimers(); resetSession(); }}>
            {t('kiosk.cancel.confirm')}
          </button>
        </div>
      </div>
    </div>
  ) : null);

  const renderPrompt = () => (prompt > 0 ? (
    <div className="k-modal">
      <div className="k-modal-card">
        <div className="k-modal-title">{t('kiosk.idle.title')}</div>
        <div className="k-count">{prompt}</div>
        <div className="k-modal-body">{t('kiosk.idle.body')}</div>
        <div className="k-btn-row">
          <button type="button" className="k-btn ghost" onClick={() => { clearTimers(); resetSession(); }}>
            {t('kiosk.idle.finish')}
          </button>
          <button type="button" className="k-btn block" onClick={poke}>
            {t('kiosk.idle.stay')}
          </button>
        </div>
      </div>
    </div>
  ) : null);

  // ------------------------------------------------------------ shell ----
  if (loading) {
    return (
      <div className="k-root">
        <div className="k-step"><p className="k-sub"><Loader2 size={40} /> {t('kiosk.loading')}</p></div>
      </div>
    );
  }
  if (error) {
    return (
      <div className="k-root">
        <div className="k-step">
          <h1 className="k-h2">{t('kiosk.error.title')}</h1>
          <p className="k-sub">{error}</p>
        </div>
      </div>
    );
  }

  const showChrome = step !== STEP.ATTRACT;
  const body = (
    <>
      {step === STEP.MENU && !sheet ? renderPills() : null}
      {step === STEP.MENU && !sheet ? renderMenu() : null}
    </>
  );

  return (
    <div className={`k-root${a11y ? ' a11y' : ''}`}
      onPointerDown={poke} onTouchStart={poke}>
      {showChrome ? renderHeader() : null}
      {/* In accessibility mode the top 40% is display only, with a monster
          up there pointing the way down, and everything a finger needs lives
          in this lower band. */}
      {a11y && showChrome ? (
        <>
          <LoweredScreenMonster />
          <div className="k-reach">
            <div className="k-reach-controls">
              {renderModeChip()}
              {renderLang()}
              {renderA11yToggle()}
              {renderCancelBtn()}
            </div>
            {body}
          </div>
        </>
      ) : body}

      {step === STEP.ATTRACT ? renderAttract() : null}
      {step === STEP.MODE ? renderMode() : null}
      {step === STEP.CART ? renderCart() : null}
      {step === STEP.WHO ? renderWho() : null}
      {step === STEP.PAY ? renderPay() : null}
      {step === STEP.DONE ? renderDone() : null}
      {sheet ? renderSheet() : null}
      {which && !sheet ? renderWhich() : null}
      {suggest && !sheet ? renderSuggest() : null}
      {renderCartBar()}
      {renderCancelPrompt()}
      {renderPrompt()}
    </div>
  );
}
